import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import SearchResults from './SearchResults';
import LocationDetails from './LocationDetails';
import { useMapStore } from '../store/mapStore';
 
const Sidebar = ({ isOpen, setIsOpen }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(50);
  const sidebarRef = useRef(null);
  const velocityRef = useRef(0);
  const lastY = useRef(0);
  const lastTime = useRef(Date.now());
 
  const isMobile = window.innerWidth <= 800;
  const { selectedLocation, setSelectedLocation } = useMapStore();
 
  const scrollRef        = useRef(null);   // the scrolling <div>
  const savedScrollTop   = useRef(null);   // last real position (null = none yet)
  const ignoreNextZero   = useRef(false);  // skip the first “fake” 0


  // ── Remember and restore scroll position (mobile) ──────────────
  useEffect(() => {
    if (!isMobile) return;
   
    const el = scrollRef.current;
    if (!el) return;
   
    if (selectedLocation) {
      // LIST → DETAILS  (selectedLocation changed from null → something)
      // savedScrollTop.current = el.scrollTop;   // remember where we were
      ignoreNextZero.current = true;           // next 0 is synthetic – skip it
    } else {
      // DETAILS → LIST  (selectedLocation just turned back to null)
      // Wait one paint so rows are rendered, then restore
      if (scrollRef.current) scrollRef.current.scrollTop = savedScrollTop.current;
    }
  }, [selectedLocation, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
   
    const velocityRef = { current: 0 };
    const lastY = { current: 0 };
    const lastTime = { current: 0 };
   
    const handleTouchStart = (e) => {
      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const rect = sidebarRef.current?.getBoundingClientRect();
      if (!rect) return;
   
      const verticalLimit = 60;
      const horizontalPadding = 20;
  const isWithinTop = touchY - rect.top <= verticalLimit;
      const isWithinX = touchX >= rect.left - horizontalPadding && touchX <= rect.right + horizontalPadding;
   
      if (isWithinTop && isWithinX) {
        setIsDragging(true);
        setStartY(touchY);
        lastY.current = touchY;
  lastTime.current = performance.now();
      }
    };
   
    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const touchY = e.touches[0].clientY;
  const now = performance.now();
      const deltaY = startY - touchY;
      const vh = Math.min(Math.max(20, currentHeight + (deltaY / window.innerHeight) * 100), 90);
      setCurrentHeight(vh);
  if (sidebarRef.current) sidebarRef.current.style.height = `${vh}vh`;
   
      const dy = touchY - lastY.current;
      const dt = now - lastTime.current;
      velocityRef.current = dy / dt;
   
      lastY.current = touchY;
      lastTime.current = now;
    };
   
    const handleTouchEnd = () => {
      if (!isDragging) return;
      setIsDragging(false);
   
      const velocity = velocityRef.current;
   
      if (velocity > 0.5) {
        // Flick down → collapse
        setCurrentHeight(20);
  if (sidebarRef.current) sidebarRef.current.style.height = `20vh`;
      } else if (velocity < -0.5) {
        // Flick up → expand
        setCurrentHeight(90);
  if (sidebarRef.current) sidebarRef.current.style.height = `90vh`;
      }
      // Else: keep the drag position (no snap)
    };
   
    const el = sidebarRef.current;
    if (!el) return;
   
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
   
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, currentHeight]);
 
  return (
    <>
      <div
        ref={sidebarRef}
        className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'} overflow-hidden flex flex-col`}
        style={
          isMobile
            ? {
                height: `${currentHeight}vh`,
                top: 'auto',
                bottom: 0,
                right: 0,
                width: '100%',
                borderRadius: '1rem 1rem 0 0',
              }
            : {
              bottom: '300px',
              right: 0,
            }
        }
      >
        {isMobile && (
          <div className="flex justify-center p-2 cursor-grab touch-none">
            <img src="assets/mobile-assets/drag-icon.svg" alt="Drag handle" className="h-6 w-8" />
          </div>
        )}
 
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          {isMobile && selectedLocation ? (
            <button onClick={() => setSelectedLocation(null)} className="p-2 -ml-2 rounded-full hover:bg-gray-100">
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : null}
 
          <div className="flex items-center">
            <h2 className="font-bold text-fluid-lg">
              {isMobile && selectedLocation ? 'Location Details' : 'Search Results'}
            </h2>
          </div>
 
          {!isMobile && (
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-full hover:bg-gray-200"
              aria-label="Collapse sidebar"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
 
        <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto"
            onScroll={() => {
              const el = scrollRef.current;
              if (!el) return;                              // should never happen
          
              const y = el.scrollTop;
          
              // --- first synthetic 0 after panel-swap ------------------
              if (y === 0 && ignoreNextZero.current) {
                el.scrollTop = savedScrollTop.current
                ignoreNextZero.current = false;             // peel the sticker
                return;                                     // do NOT touch savedScrollTop
              }
          
              // --- real user scrolling (including a real 0) ------------
              if (y !== savedScrollTop.current) {
                // console.log(`[onScroll] → store new y ${y}`);
                savedScrollTop.current = y;                 // remember
              } else {
              }
            }}
          >
          {isMobile && selectedLocation ? (
            <LocationDetails location={selectedLocation} isMobileView={true} />
          ) : (
            <SearchResults />
          )}
        </div>
      </div>
 
      {!isOpen && !isMobile && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed z-20 right-4 top-[calc(var(--header-height)+var(--customer-stats-height)+2rem)] bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          aria-label="Expand sidebar"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
    </>
  );
};
 
export default Sidebar;
 